import { EmailGateEnum } from '@zaoblako/shared/modules/integration/enums/email.gate.enum';
import { EmailGateEnumI18n } from '@zaoblako/shared/modules/integration/i18n/email.gate.enum.i18n';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { NotificationDtoKeysEnum } from '@zaoblako/shared/modules/notification/enums/notification-dto-keys.enum';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { IntegrationCheckersEnum } from '@zaoblako/shared/modules/integration/enums/integration-checkers.enum';
import { entityLikeSchemaFactory } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { emailSchema } from '@zaoblako/shared/modules/serde/schemas/email.schema';
import type { EmailFindInterface } from '@zaoblako/shared/modules/integration/dtos/email/email-find.dto';
import type { MainSmsAccountFindInterface } from '@zaoblako/shared/modules/integration/dtos/main-sms/main-sms-account-find.dto';
import type { SerdeContextInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context.interface';
import type { SerdeContextDataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context-data.interface';

import * as yup from 'yup';

/** E-Mail sender create/update request body - DTO signature */
export interface EmailSenderFormInterface {
  /** Email-gate to use */
  gate: EmailGateEnum;
  /**
   * "MainSMS" Email-gate account credentials. Required only
   * when {@link EmailSenderFormInterface.gate} property
   * equals {@link EmailGateEnum.MAIN_SMS}.
   */
  mainSmsAccount?: MainSmsAccountFindInterface;
  /**
   * "MainSMS" Email-gate selected sender email. Required only
   * when {@link EmailSenderFindDto.gate} property
   * equals {@link EmailGateEnum.MAIN_SMS}.
   */
  mainSmsAccountEmail?: string;
  /**
   * Selected sender mailbox. Required only
   * when {@link EmailSenderFormInterface.gate} property
   * equals {@link EmailGateEnum.FRM}.
   */
  email?: EmailFindInterface;
}

const requiredForGate = (gate: EmailGateEnum) => (
  context: SerdeContextInterface<SerdeContextDataInterface, EmailSenderFormInterface>,
): boolean => context?.value?.gate !== gate;

/** E-Mail sender create/update request body - DTO schema */
export const EmailSenderFormDto = SerdeDtoClass.create<EmailSenderFormInterface>(NotificationDtoKeysEnum.EMAIL_SENDER_FORM_DTO, {
  gate: {
    label: 'Тип почтового сервиса (гейт)',
    serializer: commonEnumValidator(EmailGateEnum, EmailGateEnumI18n),
  },
  mainSmsAccount: {
    optional: requiredForGate(EmailGateEnum.MAIN_SMS),
    label: 'Аккаунт "MainSMS"',
    checkers: [IntegrationCheckersEnum.MAIN_SMS_ACCOUNT_ASSERT_EXISTS_BY_DTO],
    serializer: entityLikeSchemaFactory<SerdeContextDataInterface, EmailSenderFormInterface>(context => context?.value?.gate === EmailGateEnum.MAIN_SMS),
  },
  mainSmsAccountEmail: {
    optional: requiredForGate(EmailGateEnum.MAIN_SMS),
    label: 'Email',
    serializer: (
      context: SerdeContextInterface<SerdeContextDataInterface, EmailSenderFormInterface>,
    ) => context?.value?.gate === EmailGateEnum.MAIN_SMS ? emailSchema : yup.string(),
  },
  email: {
    optional: requiredForGate(EmailGateEnum.FRM),
    label: 'Почтовый сервер',
    checkers: [IntegrationCheckersEnum.EMAIL_ASSERT_EXISTS_BY_DTO],
    serializer: entityLikeSchemaFactory<SerdeContextDataInterface, EmailSenderFormInterface>(context => context?.value?.gate === EmailGateEnum.FRM),
  },
});
